import React, {useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';

import {ConfirmBlockMessage, ConfirmRemoveFooter} from './styled';

interface Props {
    isOpen: boolean;
    toBlock: boolean;
    isPending: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => void;
}

export const ConfirmBlockModal = ({isOpen, toBlock, isPending, onClose, onConfirm}: Props) => {
    const {t} = useTranslation();
    const translate = (key: string) => t(`pages.profile.organizations.confirmBlock.${key}`);

    const [reason, setReason] = useState('');
    const [showReason, setShowReason] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setReason('');
            setShowReason(false);
        }
    }, [isOpen]);

    const buttonText = useMemo(() => {
        if (showReason) {
            return toBlock ? translate('block') : translate('unblock');
        }

        return translate('ok');
    }, [showReason, toBlock, t]);

    if (!isOpen) return null;

    return (
        <Modal
            title={toBlock ? translate('title') : translate('titleUnblock')}
            modalWidth={0}
            show={isOpen}
            closable={!isPending}
            onClose={onClose}
        >
            <ConfirmBlockMessage>
                {toBlock ? translate('message') : translate('messageUnblock')}
            </ConfirmBlockMessage>
            {showReason && (
                <Input
                    isRequired
                    type="text"
                    label={translate('reason')}
                    placeholder={translate('reasonPlaceholder')}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    style={{marginBottom: 10}}
                />
            )}
            <ConfirmRemoveFooter>
                <Button
                    isLoading={isPending}
                    disabled={isPending}
                    type="button"
                    variant={ButtonVariant.Outline}
                    onClick={onClose}
                >
                    {translate('cancel')}
                </Button>
                <Button
                    isLoading={isPending}
                    variant={ButtonVariant.Normal}
                    size="md"
                    type="button"
                    disabled={isPending || (showReason && !reason.trim())}
                    onClick={() => {
                        if (showReason) {
                            onConfirm(reason.trim());
                        } else {
                            setShowReason(true);
                        }
                    }}
                >
                    {buttonText}
                </Button>
            </ConfirmRemoveFooter>
        </Modal>
    );
};
